/**
 * The buyer's-guide page, and the scaffold for named comparisons.
 *
 * ── Why there is no "NextOpp vs Jobscan" page in here ─────────────────────
 * Comparison pages assert facts about other companies' products, and on
 * 2026-08-31 those facts could not be verified:
 *
 *   jobscan.co/pricing  301s to an authenticated app URL with no pricing in it
 *   tealhq.com/pricing  403s to automated fetching
 *   web search          returns only competitor-authored blogs, whose figures
 *                       contradict each other — the same number appeared as
 *                       both "$89.95 every 3 months" and "$89.95/mo"
 *
 * Publishing a competitor's price sourced from that would be an unverified
 * claim about a named company, taken from its rivals, on a site whose promise
 * is "nothing invented — and you can check". A wrong figure there is a
 * defamation exposure and, worse, the story writes itself: the honesty product
 * made something up.
 *
 * So this page compares on the axis that can be verified — what to ask any
 * tool, and what this one does, every answer checkable against the code. It
 * captures most of the same search intent without naming anybody falsely.
 *
 * COMPETITORS below is the scaffold for named pages. It is empty on purpose.
 * Every claim requires a source URL and a verification date, and a test fails
 * the build if one is missing, so an unsourced competitor claim cannot ship
 * even by accident.
 */

/**
 * The questions worth asking any resume tool.
 *
 * `ours` is a claim about this product and every one is checkable — the file
 * that proves it is named in `evidence`, which is for reviewers rather than
 * readers, and is what keeps this page honest as the product changes.
 */
export const CRITERIA = [
  {
    question: "Will it invent things to raise your score?",
    why: "The failure mode that ends interviews. A tool optimising a number has every incentive to add a skill you do not have, and you will not always notice before you send it.",
    ours: "No. The rewrite only rephrases, reorders and emphasises what your resume already says, and each bullet is checked afterwards — names, numbers, dates and employers in the output are matched against the input, and anything unmatched is flagged rather than shipped.",
    evidence: "frontend/src/lib/grounding.js — checkGrounding()",
  },
  {
    question: "Will it apply on your behalf?",
    why: "Bulk auto-apply gets accounts banned on job boards and puts your name on applications you never read. Ask whether the tool can submit, not whether it usually does.",
    ours: "It cannot. Drafting, autofill and tracking are in; the submit path is deliberately excluded, including from the API that agents use. You press submit on the real job board.",
    evidence: "mcp-server/README.md — safety contract; ApplicationAssistant.jsx",
  },
  {
    question: "Is the free tier a plan, or a countdown?",
    why: "“Free” usually means a trial that expires, or a scan count small enough to be a demo. Both are fine — they are just not the same thing, and the page rarely says which.",
    ours: "A standing monthly allowance that resets: a base resume, 5 tailored resumes, 5 cover letters, 5 board refreshes and 10 AI actions. The ATS check itself needs no account at all.",
    evidence: "backend/billing.py — TIERS['free'].caps",
  },
  {
    question: "Where do its jobs actually come from?",
    why: "Most tools resell the same aggregator feed, so you see a role once it has been indexed and once everyone else has seen it. Ask which boards it reads and how directly.",
    ours: "Company applicant tracking boards directly — nine of them: Greenhouse, Lever, Ashby, Workable, Workday, SmartRecruiters, Recruitee, Personio and Breezy — plus a crawled index of those boards you can search in one query.",
    evidence: "backend/job_providers.py — PROVIDERS; backend/job_index.py",
  },
  {
    question: "Does it show its working?",
    why: "A score with no reasoning cannot be argued with, which makes it useless. You need to know which words drove it and what to do next.",
    ours: "The score comes with the posting's required and preferred skills, the terms you match, the ones you are missing, the ones you cover only weakly, your seniority fit, and a plain-English explanation.",
    evidence: "backend/ai_service.py — analyze_ats_match()",
  },
  {
    question: "Can you get your data out, and delete it?",
    why: "Your resume is personal data. The question is not whether a privacy page exists but whether there is a button, and what it removes.",
    ours: "One endpoint deletes the account and everything attached to it. Resumes are stored privately and are never given a public URL.",
    evidence: "backend/routers/auth_routes.py — DELETE /account",
  },
  {
    question: "Can your own tools drive it?",
    why: "Niche, and decisive if it applies to you. Most tools are a website and nothing else.",
    ours: "A Model Context Protocol server with 26 tools, so Claude or any MCP client can read your pipeline, score a resume and draft — under the same rules, including the excluded submit path. Access is a scoped token you can revoke.",
    evidence: "mcp-server/server.py — @mcp.tool decorators",
  },
];

/**
 * Named comparisons. Empty deliberately — see the note at the top of the file.
 *
 * To add one, every `claim` needs `source` (a URL on the competitor's own
 * site, not a review blog) and `verifiedOn` (ISO date). Anything about pricing
 * should be re-checked before launch and on a schedule after it, because a
 * stale comparison is a wrong one.
 *
 * @type {Array<{slug: string, name: string, claims: Array<{claim: string, source: string, verifiedOn: string}>}>}
 */
export const COMPETITORS = [];
